/**
 * Thai Timezone Date Utility Functions
 * 
 * All dates are displayed in Thai timezone (Asia/Bangkok) regardless of server location
 */

const THAI_TIMEZONE = 'Asia/Bangkok';
const THAI_LOCALE = 'th-TH';

/**
 * Format date as Thai date: "16 ธ.ค. 2567"
 */
export const formatThaiDate = (date: string | Date | null | undefined): string => {
    if (!date) return '-';
    try {
        return new Date(date).toLocaleDateString(THAI_LOCALE, {
            timeZone: THAI_TIMEZONE,
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });
    } catch {
        return '-';
    }
};

/**
 * Format date as short Thai date: "16 ธ.ค. 67"
 */
export const formatThaiDateShort = (date: string | Date | null | undefined): string => {
    if (!date) return '-';
    try {
        return new Date(date).toLocaleDateString(THAI_LOCALE, {
            timeZone: THAI_TIMEZONE,
            year: '2-digit',
            month: 'short',
            day: 'numeric',
        });
    } catch {
        return '-';
    }
};

/**
 * Format date and time: "16 ธ.ค. 2567, 09:48"
 */
export const formatThaiDateTime = (date: string | Date | null | undefined): string => {
    if (!date) return '-';
    try {
        return new Date(date).toLocaleString(THAI_LOCALE, {
            timeZone: THAI_TIMEZONE,
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    } catch {
        return '-';
    }
};

/**
 * Format time only: "09:48"
 */
export const formatThaiTime = (date: string | Date | null | undefined): string => {
    if (!date) return '-';
    try {
        return new Date(date).toLocaleTimeString(THAI_LOCALE, {
            timeZone: THAI_TIMEZONE,
            hour: '2-digit',
            minute: '2-digit',
        });
    } catch {
        return '-';
    }
};

/**
 * Format relative time: "เมื่อวาน", "3 นาทีที่แล้ว", etc.
 */
export const formatThaiRelativeTime = (date: string | Date | null | undefined): string => {
    if (!date) return '-';
    try {
        const now = new Date();
        const target = new Date(date);
        const diffMs = now.getTime() - target.getTime();
        const diffSec = Math.floor(diffMs / 1000);
        const diffMin = Math.floor(diffSec / 60);
        const diffHour = Math.floor(diffMin / 60);
        const diffDay = Math.floor(diffHour / 24);

        if (diffSec < 60) return 'เมื่อสักครู่';
        if (diffMin < 60) return `${diffMin} นาทีที่แล้ว`;
        if (diffHour < 24) return `${diffHour} ชั่วโมงที่แล้ว`;
        if (diffDay === 1) return 'เมื่อวาน';
        if (diffDay < 7) return `${diffDay} วันที่แล้ว`;

        // Fall back to date
        return formatThaiDate(date);
    } catch {
        return '-';
    }
};

/**
 * Format as ISO date for inputs: "2024-12-16"
 */
export const formatISODate = (date: string | Date | null | undefined): string => {
    if (!date) return '';
    try {
        const d = new Date(date);
        return d.toISOString().split('T')[0];
    } catch {
        return '';
    }
};
